import React, { lazy, Suspense, useCallback, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, Save, CircleHelp, FileText, ChevronDown, FileUp, CheckCircle2, Clock3, AlertTriangle, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import LadderDropdownMenu from '../components/LadderDropdownMenu';
import FeedbackDialog from '../components/FeedbackDialog';
import CIPalette from '../components/CIPalette';
import { useNotifications } from '../contexts/NotificationContext';
import { ServiceMapData, CIItem, CIEdge, ValidationStatus, EnvironmentType } from '../types';
import { getCITypeInfo } from '../components/ITIcons';
import { mapServiceNowClassToDiagramType } from '../utils/serviceNowClassMapper';
import { getRelationshipStyle } from '../utils/relationshipStyles';
import ApiTestComponent from '../components/ApiTestComponent';
import UserAvatar from '../components/UserAvatar';
import { exportToCSV } from '../components/ControlPanel/csvExportHelpers';
import { fetchScopeSummary, ScopeSummaryResponse } from '../services/cmdb/ScopeService';

// Lazy load heavy components
const DiagramCanvas = lazy(() => import('../components/DiagramCanvas'));
const PropertiesPanel = lazy(() => import('../components/PropertiesPanel'));
const ControlPanel = lazy(() => import('../components/ControlPanel'));
const RelationshipLegend = lazy(() => import('../components/RelationshipLegend'));
const CSVImportDialog = lazy(() => import('../components/CSVImportDialog'));
const AnnotationDialog = lazy(() => import('../components/AnnotationDialog'));
const RelationshipPropertiesDialog = lazy(() => import('../components/RelationshipPropertiesDialog'));

const CSV_GUIDE_SEEN_STORAGE_KEY = 'service-mapper-csv-guide-seen';
const SUPPORT_GROUP_STORAGE_KEY = 'service-mapper-support-group';
const APPLICATION_ID_PAGE_SIZE = 10;

const mapServiceNowEnvironmentToFrontend = (environmentRaw: string): EnvironmentType | null => {
  const normalized = environmentRaw
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!normalized) {
    return null;
  }

  if (normalized.includes('pre prod') || normalized.includes('preprod') || normalized.includes('staging') || normalized.includes('stage')) {
    return 'Staging';
  }

  if (normalized.includes('qa') || normalized.includes('test') || normalized.includes('uat')) {
    return 'Test';
  }

  if (normalized.includes('disaster') || normalized === 'dr' || normalized.startsWith('dr ')) {
    return 'DR';
  }

  if (normalized.includes('dev')) {
    return 'Dev';
  }

  if (normalized.includes('prod') || normalized.includes('production')) {
    return 'Prod';
  }

  if (normalized.includes('other')) {
    return 'Other';
  }

  return null;
};

const parseImportedApplicationId = (
  rawApplicationId: string
): { applicationId: string; environment: EnvironmentType | null } => {
  const cleaned = (rawApplicationId || '')
    .replace(/[\u0000-\u001f\u007f\u200b-\u200d\ufeff]/g, '')
    .trim();

  if (!cleaned) {
    return { applicationId: '', environment: null };
  }

  const parts = cleaned.split(':-:');
  if (parts.length < 2) {
    return { applicationId: cleaned, environment: null };
  }

  const applicationId = parts[0].trim();
  const environmentRaw = parts.slice(1).join(':-:').trim();
  const environment = mapServiceNowEnvironmentToFrontend(environmentRaw);

  return {
    applicationId: applicationId || cleaned,
    environment,
  };
};

export default function ServiceMapperPage() {
  const { showToast } = useNotifications();

  const normalizeServiceNowNode = (node: CIItem): CIItem => {
    if (node.properties?.source !== 'ServiceNow') {
      return node;
    }

    const sysClassName = node.properties?.sys_class_name || node.validatedCI?.sys_class_name;
    if (!sysClassName) {
      return node;
    }

    if (node.type !== 'UNKNOWN_DEVICE' && getCITypeInfo(node.type)) {
      if (node.properties?.sys_class_name === sysClassName) {
        return node;
      }

      return {
        ...node,
        properties: {
          ...node.properties,
          sys_class_name: sysClassName,
        },
      };
    }

    const remappedType = mapServiceNowClassToDiagramType(sysClassName, node.type);

    return {
      ...node,
      type: remappedType,
      properties: {
        ...node.properties,
        sys_class_name: sysClassName,
      },
    };
  };
  
  // Validation function for imported data
  const validateServiceMapData = (data: any): data is ServiceMapData => {
    return (
      data &&
      typeof data === 'object' &&
      Array.isArray(data.nodes) &&
      Array.isArray(data.edges) &&
      data.metadata &&
      typeof data.metadata.name === 'string' &&
      typeof data.metadata.created === 'string' &&
      typeof data.metadata.lastModified === 'string'
    );
  };
  
  const [data, setData] = useState<ServiceMapData>({
    metadata: {
      name: 'New Service Map',
      created: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      anchorNodeId: null,
    },
    nodes: [],
    edges: [],
  });
  
  const [selectedNode, setSelectedNode] = useState<CIItem | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [appIdPopupOpen, setAppIdPopupOpen] = useState(true);
  const [supportGroupInput, setSupportGroupInput] = useState<string>(() => {
    try {
      return localStorage.getItem(SUPPORT_GROUP_STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [scopeSummary, setScopeSummary] = useState<ScopeSummaryResponse | null>(null);
  const [isScopeLoading, setIsScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [applicationIdPage, setApplicationIdPage] = useState(1);
  const [externalSyncTrigger, setExternalSyncTrigger] = useState(0);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [editingNodeName, setEditingNodeName] = useState('');
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(true);
  const [statusFilter, setStatusFilter] = useState<ValidationStatus | 'all' | null>(null);
  const mapperSearchQuery = '';
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false);
  const [annotationNodeId, setAnnotationNodeId] = useState<string | null>(null);

  const [showApiTest, setShowApiTest] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const [showLegend, setShowLegend] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState<string | null>(null);
  const [shouldFitToScreen, setShouldFitToScreen] = useState(false);
  const [isRightPanelBusy, setIsRightPanelBusy] = useState(false);
  const [isGuidedFlowActive, setIsGuidedFlowActive] = useState(false);
  const [hasGuidedSyncCompleted, setHasGuidedSyncCompleted] = useState(false);
  const [hasSeenCsvGuide, setHasSeenCsvGuide] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CSV_GUIDE_SEEN_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  
  // Clipboard state for copy/paste functionality
  const [clipboard, setClipboard] = useState<{
    nodes: CIItem[];
    edges: CIEdge[];
    offset: { x: number; y: number };
  } | null>(null);

  // Auto-save functionality with debouncing
  useEffect(() => {
    // Only auto-save if there's actual data (not just the initial empty state)
    if (data.nodes.length > 0 || data.edges.length > 0) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem('service-mapper-data', JSON.stringify(data));
      }, 500); // Debounce by 500ms to avoid excessive saves
      
      return () => clearTimeout(timeoutId);
    }
  }, [data]);

  const handleClearRelationships = useCallback(() => {
    const now = new Date().toISOString();
    setData(prev => ({
      ...prev,
      nodes: [],
      edges: [],
      metadata: {
        ...prev.metadata,
        anchorNodeId: null,
        lastModified: now,
      },
    }));

    setSelectedNode(null);
    setSelectedNodes(new Set());
    setStatusFilter(null);

    try {
      localStorage.removeItem('service-mapper-data');
    } catch {
      // Ignore storage failures and keep session behavior.
    }

    showToast('Map cleared. Canvas is now empty.', 'success');
  }, [showToast]);

  // Auto-load on startup
  useEffect(() => {
    const savedData = localStorage.getItem('service-mapper-data');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData) as ServiceMapData;
        // Only load if the saved data has actual content
        if (parsedData.nodes.length > 0 || parsedData.edges.length > 0) {
          const fixedNodes = parsedData.nodes.map(normalizeServiceNowNode);
          setData({ ...parsedData, nodes: fixedNodes });
          // If there's an anchor node, fit to screen on load
          if (parsedData.metadata.anchorNodeId) {
            setShouldFitToScreen(true);
          }
        }
      } catch (error) {
        // Silently handle errors - corrupted data will be ignored
      }
    }
  }, []); // Empty dependency array means this only runs once on mount

  const handleNodeAdd = useCallback((node: CIItem) => {
    setData(prev => ({
      ...prev,
      nodes: [...prev.nodes, node],
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
  }, []);

  const handleNodeUpdate = useCallback((updatedNode: CIItem) => {
    setData(prev => {
      const normalizedNode = normalizeServiceNowNode(updatedNode);
      const isAnchor = prev.metadata.anchorNodeId === updatedNode.id;
      const oldAnchor = isAnchor
        ? prev.nodes.find(n => n.id === updatedNode.id)
        : null;
      // When the anchor's App ID (serviceId) or environment changes, purge all
      // auto-discovered servicenow-* nodes and edges — they belong to the previous
      // service and must not persist when the user switches to a different App ID.
      const appIdChanged = isAnchor && oldAnchor && (
        oldAnchor.serviceId !== updatedNode.serviceId ||
        oldAnchor.environment !== updatedNode.environment
      );

      let nodes = prev.nodes.map(node =>
        node.id === normalizedNode.id ? normalizedNode : node
      );
      let edges = prev.edges;

      if (appIdChanged) {
        nodes = nodes.filter(n => !n.id.startsWith('servicenow-'));
        edges = edges.filter(e => !e.id.startsWith('servicenow-edge-'));
      }

      return {
        ...prev,
        nodes,
        edges,
        metadata: {
          ...prev.metadata,
          lastModified: new Date().toISOString(),
        },
      };
    });
    
    // Update selectedNode if it's the same node being updated
    setSelectedNode(prev => prev?.id === updatedNode.id ? normalizeServiceNowNode(updatedNode) : prev);
  }, []);

  const handleEdgeUpdate = useCallback((edgeId: string, updates: Partial<CIEdge>) => {
    setData(prev => ({
      ...prev,
      edges: prev.edges.map(edge => 
        edge.id === edgeId ? { ...edge, ...updates } : edge
      ),
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
  }, []);

  const handleSetAnchorNode = useCallback((nodeId: string | null) => {
    setData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        anchorNodeId: nodeId,
        lastModified: new Date().toISOString(),
      },
    }));
  }, []);

  const handleEditingStateChange = useCallback((nodeId: string | null, nodeName: string) => {
    setEditingNodeId(nodeId);
    setEditingNodeName(nodeName);
  }, []);

  const handleNodeDelete = useCallback((nodeId: string) => {
    setData(prev => {
      // Check if the deleted node was the anchor
      const newAnchorId = prev.metadata.anchorNodeId === nodeId ? null : prev.metadata.anchorNodeId;
      
      return {
        ...prev,
        nodes: prev.nodes.filter(node => node.id !== nodeId),
        edges: prev.edges.filter(edge => 
          edge.source !== nodeId && edge.target !== nodeId
        ),
        metadata: {
          ...prev.metadata,
          anchorNodeId: newAnchorId,
          lastModified: new Date().toISOString(),
        },
      };
    });
    
    setSelectedNodes(prev => {
      const newSet = new Set(prev);
      newSet.delete(nodeId);
      return newSet;
    });
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  }, [selectedNode]);

  const handleEdgeAdd = useCallback((
    source: string, 
    target: string, 
    connectionType?: 'straight' | 'right-angled' | 'curved',
    sourceHandleId?: string,
    targetHandleId?: string
  ) => {
    // Prevent duplicate connections between the same two nodes
    const existingEdge = data.edges.find(edge => 
      (edge.source === source && edge.target === target) || 
      (edge.source === target && edge.target === source)
    );

    if (existingEdge) {
      return; // Don't create duplicate connection
    }

    // Force the anchor to always be the relationship "parent" (source), regardless of
    // which node the user dragged from. This matches the direction ServiceNow-synced
    // edges already use and the backend's anchor-outgoing convention
    // (ServiceNowRelationshipService.IsAnchorOutgoing) - otherwise a connection drawn
    // FROM a child TO the anchor silently submits the anchor as the "child" in the
    // ServiceNow ticket, which sync/delete logic then can't recognize as anchor-owned.
    const anchorNodeId = data.metadata.anchorNodeId;
    const shouldReverse = Boolean(anchorNodeId) && target === anchorNodeId && source !== anchorNodeId;
    const finalSource = shouldReverse ? target : source;
    const finalTarget = shouldReverse ? source : target;
    const finalSourceHandleId = shouldReverse ? targetHandleId : sourceHandleId;
    const finalTargetHandleId = shouldReverse ? sourceHandleId : targetHandleId;

    const newEdge: CIEdge = {
      id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: finalSource,
      target: finalTarget,
      relationshipType: 'depends_on', // Set a safe, default relationship type
      connectionType: connectionType || 'curved',
      sourceHandleId: finalSourceHandleId,
      targetHandleId: finalTargetHandleId,
      created: new Date().toISOString(),
      lastModified: new Date().toISOString(),
    };

    setData(prev => ({
      ...prev,
      edges: [...prev.edges, newEdge],
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
  }, [data.edges, data.metadata.anchorNodeId]);


  const handleEdgeDelete = useCallback((edgeId: string) => {
    setData(prev => ({
      ...prev,
      edges: prev.edges.filter(edge => edge.id !== edgeId),
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
  }, []);

  const handleOpenAnnotationDialog = useCallback((nodeId: string) => {
    setAnnotationNodeId(nodeId);
    setAnnotationDialogOpen(true);
  }, []);

  const handleSaveComment = useCallback((nodeId: string, comments: string) => {
    setData(prev => ({
      ...prev,
      nodes: prev.nodes.map(node =>
        node.id === nodeId 
          ? { ...node, comments, lastModified: new Date().toISOString() }
          : node
      ),
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
  }, []);

  const handleLoadScopeSummary = useCallback(async (groupOverride?: string) => {
    const group = (groupOverride ?? supportGroupInput).trim();
    if (!group) {
      setScopeSummary(null);
      setScopeError('Support Group is required to load Application IDs.');
      return;
    }

    setIsScopeLoading(true);
    setScopeError(null);

    try {
      const summary = await fetchScopeSummary(group);
      setScopeSummary(summary);
      try {
        localStorage.setItem(SUPPORT_GROUP_STORAGE_KEY, group);
      } catch {
        // Ignore storage failures and keep session behavior.
      }
    } catch (error: any) {
      setScopeSummary(null);
      setScopeError(error?.message ?? 'Failed to load Application IDs for this support group.');
    } finally {
      setIsScopeLoading(false);
    }
  }, [supportGroupInput]);

  useEffect(() => {
    if (!appIdPopupOpen) {
      return;
    }

    if (!supportGroupInput.trim()) {
      return;
    }

    void handleLoadScopeSummary(supportGroupInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIdPopupOpen]);

  const applicationIdRows = React.useMemo(() => {
    const rows = (scopeSummary?.services ?? [])
      .filter((service) => Boolean(service.applicationId?.trim()))
      .map((service) => ({
        applicationId: service.applicationId,
        serviceSysId: service.sysId,
        serviceName: service.name,
        status: service.overallStatus,
        coveragePercent: service.coveragePercent,
      }));

    return rows.sort((a, b) => a.applicationId.localeCompare(b.applicationId));
  }, [scopeSummary]);

  useEffect(() => {
    setApplicationIdPage(1);
  }, [scopeSummary]);

  const totalApplicationIdPages = Math.max(
    1,
    Math.ceil(applicationIdRows.length / APPLICATION_ID_PAGE_SIZE)
  );

  const paginatedApplicationIdRows = React.useMemo(() => {
    const startIndex = (applicationIdPage - 1) * APPLICATION_ID_PAGE_SIZE;
    return applicationIdRows.slice(startIndex, startIndex + APPLICATION_ID_PAGE_SIZE);
  }, [applicationIdRows, applicationIdPage]);

  const visibleRowsStart = applicationIdRows.length === 0
    ? 0
    : (applicationIdPage - 1) * APPLICATION_ID_PAGE_SIZE + 1;
  const visibleRowsEnd = Math.min(
    applicationIdPage * APPLICATION_ID_PAGE_SIZE,
    applicationIdRows.length
  );

  const handleLoadMappingForApplicationId = useCallback((row: {
    applicationId: string;
    serviceSysId: string;
    serviceName: string;
  }) => {
    const now = new Date().toISOString();
    const parsedApplication = parseImportedApplicationId(row.applicationId);
    const anchorApplicationId = parsedApplication.applicationId || row.applicationId;

    setData((prev) => {
      // Loading an Application ID always starts a fresh board: clear all existing
      // nodes/edges and create only the new anchor. Sync will populate the rest.
      const newAnchorId = `anchor-${Date.now()}`;
      const newAnchorNode: CIItem = {
        id: newAnchorId,
        name: row.serviceName || anchorApplicationId,
        type: 'APPLICATION',
        status: 'active',
        description: 'Application Service loaded from Support Group scope list',
        properties: {
          source: 'ScopePopup',
          sys_class_name: 'cmdb_ci_service_auto',
        },
        position: { x: 420, y: 260 },
        validationStatus: 'valid',
        validatedCI: {
          sys_id: row.serviceSysId,
          name: row.serviceName || row.applicationId,
          sys_class_name: 'cmdb_ci_service_auto',
          operational_status: '1',
        },
        isAnchor: true,
        serviceId: anchorApplicationId,
        environment: parsedApplication.environment ?? 'Prod',
        created: now,
        lastModified: now,
      };

      return {
        ...prev,
        nodes: [newAnchorNode],
        edges: [],
        metadata: {
          ...prev.metadata,
          anchorNodeId: newAnchorId,
          lastModified: now,
        },
      };
    });

    setHasGuidedSyncCompleted(false);
    setAppIdPopupOpen(false);
    setExternalSyncTrigger((prev) => prev + 1);
    showToast(`Loading mapping for ${row.applicationId}...`, 'success');
  }, [showToast]);


  const handleDataImport = useCallback((importedData: ServiceMapData) => {
    // Get the coordinate offset from metadata, or use default if not available
    const coordinateOffset = importedData.metadata?.coordinateOffset || { x: 1000, y: 1000 };
    
    // Normalize coordinates back to internal coordinate system
    const normalizedData = {
      ...importedData,
      nodes: importedData.nodes.map(node => normalizeServiceNowNode({
        ...node,
        position: {
          x: node.position.x - coordinateOffset.x,
          y: node.position.y - coordinateOffset.y
        }
      })),
      // Remove the coordinate offset from metadata as it's no longer needed
      metadata: {
        ...importedData.metadata,
        coordinateOffset: undefined
      }
    };
    setData(normalizedData);
    setSelectedNode(null);
    setSelectedNodes(new Set());
    setHasGuidedSyncCompleted(false);
    
    // Trigger fit-to-screen for CSV imports
    if (importedData.nodes.length > 0) {
      setShouldFitToScreen(true);
    }
  }, []);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string;
          const importedData = JSON.parse(content);

          if (!validateServiceMapData(importedData)) {
            showToast('Invalid project file format. Please ensure it\'s a valid Service Mapper project.', 'error');
            return;
          }

          if (!importedData.nodes.every((node: any) =>
            node &&
            typeof node.id === 'string' &&
            typeof node.name === 'string' &&
            node.position &&
            typeof node.position.x === 'number' &&
            typeof node.position.y === 'number'
          )) {
            showToast('Invalid node data in project file. Some nodes are missing required properties.', 'error');
            return;
          }

          if (!importedData.edges.every((edge: any) =>
            edge &&
            typeof edge.id === 'string' &&
            typeof edge.source === 'string' &&
            typeof edge.target === 'string'
          )) {
            showToast('Invalid edge data in project file. Some connections are missing required properties.', 'error');
            return;
          }

          handleDataImport(importedData);
          showToast('Project loaded successfully!', 'success');
        } catch (error) {
          console.error('Import error:', error);
          showToast('Error importing file. Please check the format and try again.', 'error');
        }
      };

      reader.readAsText(file);
    };

    input.click();
  }, [handleDataImport, showToast]);

  const handleFitToScreenComplete = useCallback(() => {
    setShouldFitToScreen(false);
  }, []);

  const handleFocusAnchorForAppId = useCallback(() => {
    const anchorId = data.metadata.anchorNodeId;
    if (!anchorId) {
      showToast('Set an anchor component first to add Application ID.', 'warning');
      return;
    }

    const anchorNode = data.nodes.find((node) => node.id === anchorId) || null;
    if (!anchorNode) {
      showToast('Anchor component not found in the current map.', 'warning');
      return;
    }

    setSelectedNode(anchorNode);
    setSelectedNodes(new Set([anchorNode.id]));
    setShouldFitToScreen(true);
  }, [data.metadata.anchorNodeId, data.nodes, showToast]);

  // Helper function to generate user-friendly filenames
  const generateFilename = useCallback((baseName: string, extension: string) => {
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format
    return `Service_Map_${timestamp}.${extension}`;
  }, []);

  const handleDataExport = useCallback(() => {
    // Find the minimum coordinates to normalize to positive values
    const minX = Math.min(0, ...data.nodes.map(node => node.position.x));
    const minY = Math.min(0, ...data.nodes.map(node => node.position.y));
    const offsetX = Math.abs(minX) + 100; // Add 100px padding
    const offsetY = Math.abs(minY) + 100;
    
    const normalizedData = {
      ...data,
      nodes: data.nodes.map(node => ({
        ...node,
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY
        }
      })),
      // Store the offset for import
      metadata: {
        ...data.metadata,
        coordinateOffset: { x: offsetX, y: offsetY }
      }
    };
    return normalizedData;
  }, [data]);

  // Export handlers for ladder dropdown
  const handleExportJSON = useCallback(async () => {
    const exportData = handleDataExport();
    
    const anchorNode = exportData.nodes.find(node => node.id === exportData.metadata.anchorNodeId);
    if (anchorNode && (!anchorNode.serviceId || anchorNode.serviceId.trim() === '')) {
      showToast('Warning: Anchor component is missing Application ID. This may cause issues when importing to ServiceNow.', 'warning');
    }
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const suggestedName = generateFilename('service-map', 'json');
    
    if ('showSaveFilePicker' in window && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [{ description: 'Service Map JSON Files', accept: { 'application/json': ['.json'] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        showToast('Project exported successfully!', 'success');
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') { 
          return; 
        }
        showToast('Error saving file.', 'error');
        console.error(err);
      }
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Project downloaded successfully!', 'success');
    }
  }, [handleDataExport, showToast, generateFilename]);

  const handleExportCSV = useCallback(async () => {
    const exportData = handleDataExport();
    await exportToCSV(
      exportData,
      (message) => showToast(message, 'success'),
      (message) => showToast(message, 'error')
    );
  }, [handleDataExport, showToast]);

  // Clipboard functions for copy/paste/cut
  const handleCopy = useCallback(() => {
    if (selectedNodes.size === 0) return;
    
    const nodesToCopy = Array.from(selectedNodes).map(id => data.nodes.find(n => n.id === id)).filter(Boolean) as CIItem[];
    const edgesToCopy = data.edges.filter(edge => 
      selectedNodes.has(edge.source) && selectedNodes.has(edge.target)
    );
    
    // Calculate the offset for pasting (center of selection)
    const minX = Math.min(...nodesToCopy.map(n => n.position.x));
    const minY = Math.min(...nodesToCopy.map(n => n.position.y));
    const maxX = Math.max(...nodesToCopy.map(n => n.position.x));
    const maxY = Math.max(...nodesToCopy.map(n => n.position.y));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    setClipboard({
      nodes: nodesToCopy,
      edges: edgesToCopy,
      offset: { x: centerX, y: centerY }
    });
    
    // No notification for copy - silent operation
  }, [selectedNodes, data.nodes, data.edges]);

  const handlePaste = useCallback((pastePosition?: { x: number; y: number }) => {
    if (!clipboard) return;
    
    const offsetX = pastePosition ? pastePosition.x - clipboard.offset.x : 20;
    const offsetY = pastePosition ? pastePosition.y - clipboard.offset.y : 20;
    
    // Generate new IDs for copied nodes
    const idMap = new Map<string, string>();
    const newNodes = clipboard.nodes.map(node => {
      const newId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      idMap.set(node.id, newId);
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + offsetX,
          y: node.position.y + offsetY
        },
        lastModified: new Date().toISOString()
      };
    });
    
    // Update edge references with new node IDs
    const newEdges = clipboard.edges.map(edge => ({
      ...edge,
      id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target,
      lastModified: new Date().toISOString()
    }));
    
    setData(prev => ({
      ...prev,
      nodes: [...prev.nodes, ...newNodes],
      edges: [...prev.edges, ...newEdges],
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
    
    // Select the newly pasted nodes
    setSelectedNodes(new Set(newNodes.map(n => n.id)));
    setSelectedNode(newNodes[0] || null);
    
    // No notification for paste - silent operation
  }, [clipboard]);

  const handleCut = useCallback(() => {
    if (selectedNodes.size === 0) return;
    
    // First copy to clipboard
    handleCopy();
    
    // Then delete the selected nodes
    const edgesToDelete = data.edges.filter(edge => 
      selectedNodes.has(edge.source) || selectedNodes.has(edge.target)
    );
    
    setData(prev => ({
      ...prev,
      nodes: prev.nodes.filter(node => !selectedNodes.has(node.id)),
      edges: prev.edges.filter(edge => !edgesToDelete.some(e => e.id === edge.id)),
      metadata: {
        ...prev.metadata,
        lastModified: new Date().toISOString(),
      },
    }));
    
    setSelectedNodes(new Set());
    setSelectedNode(null);
    
    // No notification for cut - silent operation
  }, [selectedNodes, data.edges, handleCopy]);

  // Export functions for different formats
  const handleExportSVG = useCallback(async () => {
    try {
      const nodes = data.nodes;
      if (nodes.length === 0) {
        showToast('No components to export', 'warning');
        return;
      }

      const NODE_SIZE = 64;
      const LABEL_OFFSET = 18; // px below node bottom to center of label text
      const padding = 80;

      // Bounding box of all nodes
      const minX = Math.min(...nodes.map(n => n.position.x)) - padding;
      const maxX = Math.max(...nodes.map(n => n.position.x + NODE_SIZE)) + padding;
      const minY = Math.min(...nodes.map(n => n.position.y)) - padding;
      const maxY = Math.max(...nodes.map(n => n.position.y + NODE_SIZE + LABEL_OFFSET + 8)) + padding;
      const svgWidth = maxX - minX;
      const svgHeight = maxY - minY;

      // Escape XML special characters
      const esc = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

      // Truncate long labels
      const truncate = (s: string, max = 20) =>
        s.length > max ? s.slice(0, max - 1) + '\u2026' : s;

      // Build a category-appropriate geometric icon within a 32×32 display box,
      // centered at (cx, cy). All coordinates are in final SVG space.
      const buildIconSvg = (type: string, color: string, cx: number, cy: number, iconSize: number): string => {
        const sc = iconSize / 24; // scale factor: design is on 24-unit grid
        const t = (v: number) => +(v * sc).toFixed(2);

        // Servers: three horizontal bars with status dot
        const serverTypes = ['PHYSICAL_SERVER', 'VIRTUAL_MACHINE', 'VCENTER_SERVER', 'ESXI_HOST', 'STORAGE_ARRAY'];
        if (serverTypes.includes(type)) {
          const bw = t(17), bh = t(3.5), rx = t(1);
          const bx = cx - bw / 2;
          return [
            `<rect x="${bx}" y="${cy - t(8)}" width="${bw}" height="${bh}" rx="${rx}" fill="${color}"/>`,
            `<rect x="${bx}" y="${cy - t(2)}" width="${bw}" height="${bh}" rx="${rx}" fill="${color}"/>`,
            `<rect x="${bx}" y="${cy + t(4)}" width="${bw}" height="${bh}" rx="${rx}" fill="${color}"/>`,
            `<circle cx="${cx + t(7)}" cy="${cy - t(6.25)}" r="${t(1.5)}" fill="white"/>`,
            `<circle cx="${cx + t(7)}" cy="${cy - t(0.25)}" r="${t(1.5)}" fill="white"/>`,
            `<circle cx="${cx + t(7)}" cy="${cy + t(5.75)}" r="${t(1.5)}" fill="white"/>`,
          ].join('');
        }

        // Web server / Language icon: globe lines
        if (type === 'WEB_SERVER') {
          const r = t(9);
          return [
            `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${color}" stroke-width="${t(2)}"/>`,
            `<ellipse cx="${cx}" cy="${cy}" rx="${t(5)}" ry="${r}" fill="none" stroke="${color}" stroke-width="${t(1.5)}"/>`,
            `<line x1="${cx - r}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="${color}" stroke-width="${t(1.5)}"/>`,
            `<line x1="${cx}" y1="${cy - r}" x2="${cx}" y2="${cy + r}" stroke="${color}" stroke-width="${t(1.5)}"/>`,
          ].join('');
        }

        // Applications / Web Application: 3×3 dot grid
        const appTypes = ['APPLICATION', 'WEB_APPLICATION', 'MES'];
        if (appTypes.includes(type)) {
          const cs = t(4.5), gap = t(2);
          const startX = cx - cs * 1.5 - gap;
          const startY = cy - cs * 1.5 - gap;
          const cells: string[] = [];
          for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 3; c++) {
              cells.push(`<rect x="${startX + c * (cs + gap)}" y="${startY + r * (cs + gap)}" width="${cs}" height="${cs}" rx="${t(1)}" fill="${color}"/>`);
            }
          }
          return cells.join('');
        }

        // Databases: cylinder (two ellipses + rectangle)
        const dbTypes = ['DATABASE_INSTANCE', 'DATABASE_SERVER', 'DATABASE_CLUSTER', 'HISTORIAN'];
        if (dbTypes.includes(type)) {
          const ew = t(16), eh = t(5), bodyH = t(9);
          return [
            `<rect x="${cx - ew / 2}" y="${cy - bodyH / 2}" width="${ew}" height="${bodyH}" fill="${color}"/>`,
            `<ellipse cx="${cx}" cy="${cy - bodyH / 2}" rx="${ew / 2}" ry="${eh / 2}" fill="${color}"/>`,
            `<ellipse cx="${cx}" cy="${cy + bodyH / 2}" rx="${ew / 2}" ry="${eh / 2}" fill="${color}"/>`,
            `<ellipse cx="${cx}" cy="${cy - bodyH / 2}" rx="${ew / 2}" ry="${eh / 2}" fill="white" fill-opacity="0.25"/>`,
          ].join('');
        }

        // Network devices: hub with radiating spokes and outer nodes
        const netTypes = ['SWITCH', 'ROUTER', 'LOAD_BALANCER', 'WAP', 'INDUSTRIAL_SWITCH', 'INDUSTRIAL_GATEWAY', 'DATA_DIODE'];
        if (netTypes.includes(type)) {
          const cr = t(4), lr = t(9);
          const angles = [0, 60, 120, 180, 240, 300];
          const spokes = angles.map(a => {
            const rad = (a * Math.PI) / 180;
            const x1 = +(cx + cr * Math.cos(rad)).toFixed(2);
            const y1 = +(cy + cr * Math.sin(rad)).toFixed(2);
            const x2 = +(cx + lr * Math.cos(rad)).toFixed(2);
            const y2 = +(cy + lr * Math.sin(rad)).toFixed(2);
            return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="${t(2)}" stroke-linecap="round"/><circle cx="${x2}" cy="${y2}" r="${t(2)}" fill="${color}"/>`;
          }).join('');
          return `<circle cx="${cx}" cy="${cy}" r="${cr}" fill="${color}"/>${spokes}`;
        }

        // Firewall / Security: shield outline with fill
        if (type === 'FIREWALL') {
          const sw = t(16), sh = t(18);
          const sx = cx - sw / 2, sy = cy - sh / 2;
          return `<path d="M ${cx} ${sy} L ${sx + sw} ${sy + sh * 0.2} L ${sx + sw} ${sy + sh * 0.55} Q ${cx + sw * 0.45} ${sy + sh * 0.95} ${cx} ${sy + sh} Q ${cx - sw * 0.45} ${sy + sh * 0.95} ${sx} ${sy + sh * 0.55} L ${sx} ${sy + sh * 0.2} Z" fill="${color}" opacity="0.9"/>`;
        }

        // Cloud services: cloud blob
        const cloudTypes = ['CLOUD_SERVICE', 'CLOUD_INSTANCE', 'CLOUD_STORAGE'];
        if (cloudTypes.includes(type)) {
          const cw = t(18), ch = t(12);
          return `<path d="M ${cx - t(4)} ${cy + ch / 2} a${t(5)} ${t(5)} 0 0 1 ${t(-2)}-${t(9)} a${t(4)} ${t(4)} 0 0 1 ${t(7)}-${t(2)} a${t(5)} ${t(5)} 0 0 1 ${t(9)} ${t(3)} a${t(4)} ${t(4)} 0 0 1 ${t(-1)} ${t(8)} Z" fill="${color}"/>`;
        }

        // OT supervisory / field devices: gear (circle + ring)
        const otTypes = ['SCADA_SERVER', 'HMI', 'ENGINEERING_WORKSTATION', 'DCS_CONTROLLER', 'PLC', 'RTU', 'SENSOR', 'ACTUATOR', 'MOTOR', 'VALVE'];
        if (otTypes.includes(type)) {
          return [
            `<circle cx="${cx}" cy="${cy}" r="${t(9)}" fill="${color}"/>`,
            `<circle cx="${cx}" cy="${cy}" r="${t(5.5)}" fill="white" fill-opacity="0.3"/>`,
            `<circle cx="${cx}" cy="${cy}" r="${t(2.5)}" fill="white" fill-opacity="0.5"/>`,
          ].join('');
        }

        // IIoT / Edge devices: signal waves
        const iiotTypes = ['IIOT_GATEWAY', 'EDGE_COMPUTER', 'SMART_SENSOR', 'INDUSTRIAL_ROBOT', 'AGV', 'ASSET_TRACKER', 'WEARABLE_DEVICE', 'VIDEO_CAMERA', 'NETWORK_SENSOR', 'BLUETOOTH_DEVICE'];
        if (iiotTypes.includes(type)) {
          const r1 = t(5), r2 = t(8), r3 = t(11);
          return [
            `<circle cx="${cx}" cy="${cy}" r="${t(2.5)}" fill="${color}"/>`,
            `<path d="M ${cx - r1 * 0.7} ${cy + r1 * 0.7} a${r1} ${r1} 0 0 1 0-${r1 * 1.4}" fill="none" stroke="${color}" stroke-width="${t(2)}" stroke-linecap="round"/>`,
            `<path d="M ${cx + r1 * 0.7} ${cy + r1 * 0.7} a${r1} ${r1} 0 0 0 0-${r1 * 1.4}" fill="none" stroke="${color}" stroke-width="${t(2)}" stroke-linecap="round"/>`,
            `<path d="M ${cx - r2 * 0.7} ${cy + r2 * 0.7} a${r2} ${r2} 0 0 1 0-${r2 * 1.4}" fill="none" stroke="${color}" stroke-width="${t(1.5)}" stroke-linecap="round" opacity="0.6"/>`,
            `<path d="M ${cx + r2 * 0.7} ${cy + r2 * 0.7} a${r2} ${r2} 0 0 0 0-${r2 * 1.4}" fill="none" stroke="${color}" stroke-width="${t(1.5)}" stroke-linecap="round" opacity="0.6"/>`,
          ].join('');
        }

        // OT industrial / physical: box with internal grid
        const otPhysTypes = ['CONTROL_CABINET', 'PRODUCTION_LINE', 'INDUSTRIAL_ROBOT', 'SCADA_SERVER'];
        if (otPhysTypes.includes(type)) {
          const bw = t(16), bh = t(14);
          return [
            `<rect x="${cx - bw / 2}" y="${cy - bh / 2}" width="${bw}" height="${bh}" rx="${t(2)}" fill="${color}"/>`,
            `<line x1="${cx}" y1="${cy - bh / 2}" x2="${cx}" y2="${cy + bh / 2}" stroke="white" stroke-width="${t(1)}" opacity="0.4"/>`,
            `<line x1="${cx - bw / 2}" y1="${cy}" x2="${cx + bw / 2}" y2="${cy}" stroke="white" stroke-width="${t(1)}" opacity="0.4"/>`,
          ].join('');
        }

        // Default: colored circle with inner ring
        return [
          `<circle cx="${cx}" cy="${cy}" r="${t(9)}" fill="${color}"/>`,
          `<circle cx="${cx}" cy="${cy}" r="${t(5)}" fill="white" fill-opacity="0.25"/>`,
        ].join('');
      };

      // ---- Edges ----
      const edgeMarkers: string[] = [];
      const edgePaths: string[] = [];

      data.edges.forEach(edge => {
        const src = nodes.find(n => n.id === edge.source);
        const tgt = nodes.find(n => n.id === edge.target);
        if (!src || !tgt) return;

        const relStyle = getRelationshipStyle(edge.relationshipType);
        const color = relStyle.color;
        const markerId = `arrow_${edge.id.replace(/[^a-zA-Z0-9]/g, '_')}`;

        // Center positions in SVG coordinate space
        const sx = src.position.x + NODE_SIZE / 2 - minX;
        const sy = src.position.y + NODE_SIZE / 2 - minY;
        const tx = tgt.position.x + NODE_SIZE / 2 - minX;
        const ty = tgt.position.y + NODE_SIZE / 2 - minY;

        // Control points matching Connections.tsx logic
        const dx = tx - sx, dy = ty - sy;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const baseOffset = Math.min(distance * 0.4, 150);
        let cp1x: number, cp1y: number, cp2x: number, cp2y: number;
        if (Math.abs(dx) > Math.abs(dy)) {
          cp1x = sx + (dx > 0 ? baseOffset : -baseOffset); cp1y = sy;
          cp2x = tx + (dx > 0 ? -baseOffset : baseOffset); cp2y = ty;
        } else {
          cp1x = sx; cp1y = sy + (dy > 0 ? baseOffset : -baseOffset);
          cp2x = tx; cp2y = ty + (dy > 0 ? -baseOffset : baseOffset);
        }

        // Shorten endpoint to sit on the node border
        const endAngle = Math.atan2(ty - cp2y, tx - cp2x);
        const halfNode = NODE_SIZE / 2;
        const endX = +(tx - halfNode * Math.cos(endAngle)).toFixed(2);
        const endY = +(ty - halfNode * Math.sin(endAngle)).toFixed(2);

        edgeMarkers.push(
          `<marker id="${markerId}" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">` +
          `<polygon points="0 0, 10 3.5, 0 7" fill="${color}"/>` +
          `</marker>`
        );
        edgePaths.push(
          `<path d="M ${+sx.toFixed(2)} ${+sy.toFixed(2)} C ${+cp1x.toFixed(2)} ${+cp1y.toFixed(2)}, ${+cp2x.toFixed(2)} ${+cp2y.toFixed(2)}, ${endX} ${endY}"` +
          ` stroke="${color}" stroke-width="2" fill="none" opacity="0.85" marker-end="url(#${markerId})"/>`
        );
      });

      // ---- Nodes ----
      const nodeSvgs = nodes.map(node => {
        const ciInfo = getCITypeInfo(node.type);
        const color = ciInfo?.color || '#607D8B';
        const nx = +(node.position.x - minX).toFixed(2);
        const ny = +(node.position.y - minY).toFixed(2);
        const cx = +(nx + NODE_SIZE / 2).toFixed(2);
        const cy = +(ny + NODE_SIZE / 2).toFixed(2);

        const borderColor = (() => {
          switch (node.validationStatus) {
            case 'valid': return '#16a34a';
            case 'invalid': case 'not-found': return '#dc2626';
            case 'validating': return '#2563eb';
            case 'needs-selection': return '#d97706';
            default: return color;
          }
        })();

        const iconSvg = buildIconSvg(node.type, color, cx, cy, 32);
        const displayName = esc(truncate(node.name));

        return [
          `<g>`,
          `  <rect x="${nx}" y="${ny}" width="${NODE_SIZE}" height="${NODE_SIZE}" rx="16" ry="16"`,
          `    fill="#ffffff" stroke="${borderColor}" stroke-width="2" filter="url(#nodeShadow)"/>`,
          `  ${iconSvg}`,
          `  <text x="${cx}" y="${+(ny + NODE_SIZE + LABEL_OFFSET).toFixed(2)}" text-anchor="middle"`,
          `    font-family="Arial, Helvetica, sans-serif" font-size="12" fill="#1e293b" font-weight="500">${displayName}</text>`,
          `</g>`,
        ].join('\n');
      });

      // ---- Assemble SVG document ----
      const svgContent = [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">`,
        '  <defs>',
        '    <filter id="nodeShadow" x="-25%" y="-25%" width="150%" height="150%">',
        '      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.15)"/>',
        '    </filter>',
        ...edgeMarkers.map(m => `    ${m}`),
        '  </defs>',
        `  <rect width="${svgWidth}" height="${svgHeight}" fill="#f8fafc"/>`,
        ...edgePaths.map(p => `  ${p}`),
        ...nodeSvgs,
        '</svg>',
      ].join('\n');

      const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
      const suggestedName = generateFilename('service-map', 'svg');

      if ('showSaveFilePicker' in window && (window as any).showSaveFilePicker) {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName,
            types: [{ description: 'SVG Files', accept: { 'image/svg+xml': ['.svg'] } }],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          showToast('SVG exported successfully!', 'success');
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          showToast('Error saving SVG file.', 'error');
          console.error(err);
        }
      } else {
        // Fallback: object URL download
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = suggestedName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        showToast('SVG exported successfully!', 'success');
      }
    } catch (error) {
      console.error('SVG export error:', error);
      showToast('Error exporting SVG', 'error');
    }
  }, [showToast, data.nodes, data.edges, generateFilename]);

  const handleExportPNG = useCallback(async () => {
    try {
      // Find the canvas container
      const canvasElement = document.querySelector('.canvas-content') as HTMLElement;
      if (!canvasElement) {
        showToast('Canvas not found for export', 'error');
        return;
      }

      const nodes = data.nodes;
      if (nodes.length === 0) {
        showToast('No components to export', 'warning');
        return;
      }

      // Use the same bounds calculation as SVG export for consistency
      const NODE_WIDTH = 120;
      const NODE_HEIGHT = 80;
      const minX = Math.min(...nodes.map(node => node.position.x));
      const maxX = Math.max(...nodes.map(node => node.position.x + NODE_WIDTH));
      const minY = Math.min(...nodes.map(node => node.position.y));
      const maxY = Math.max(...nodes.map(node => node.position.y + NODE_HEIGHT));

      // Add padding around the content
      const padding = 100;
      const contentWidth = maxX - minX + (padding * 2);
      const contentHeight = maxY - minY + (padding * 2);

      // Calculate the offset to center the content
      const offsetX = -minX + padding;
      const offsetY = -minY + padding;

      // Temporarily adjust the canvas transform to show all content
      const originalTransform = canvasElement.style.transform;
      const originalWidth = canvasElement.style.width;
      const originalHeight = canvasElement.style.height;
      try {
        // Set the canvas to show all content
        canvasElement.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(1)`;
        canvasElement.style.width = `${contentWidth}px`;
        canvasElement.style.height = `${contentHeight}px`;

        const suggestedName = generateFilename('service-map', 'png');
        let saveFileHandle: any = null;

        // Request the native Save As dialog first while user activation is still valid.
        if ('showSaveFilePicker' in window && window.showSaveFilePicker) {
          try {
            saveFileHandle = await window.showSaveFilePicker({
              suggestedName,
              types: [{ description: 'PNG Files', accept: { 'image/png': ['.png'] } }],
            });
          } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
              return;
            }
            console.warn('showSaveFilePicker unavailable or failed early; using fallback download.', err);
          }
        }

        // Export directly to PNG to avoid SVG parsing issues.
        const domToImage = await import('dom-to-image');
        let blob: Blob;
        try {
          blob = await domToImage.toBlob(canvasElement, {
            quality: 1,
            bgcolor: '#0f172a',
            width: contentWidth,
            height: contentHeight,
            cacheBust: true,
          });
        } catch {
          // Fallback for environments where toBlob can fail.
          const pngDataUrl = await domToImage.toPng(canvasElement, {
            quality: 1,
            bgcolor: '#0f172a',
            width: contentWidth,
            height: contentHeight,
            cacheBust: true,
          });
          const response = await fetch(pngDataUrl);
          blob = await response.blob();
        }

        if (!blob || blob.size === 0) {
          throw new Error('Failed to generate PNG blob');
        }

        const downloadBlobFallback = async () => {
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = suggestedName;
          link.href = objectUrl;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          await copyBlobToClipboard();
          showToast('PNG exported successfully! (Downloaded via browser)', 'success');
        };

        const copyBlobToClipboard = async () => {
          try {
            if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
              await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob })
              ]);
            }
          } catch {
            // Clipboard writes may be denied by browser permissions.
          }
        };

        if (saveFileHandle) {
          try {
            const writable = await saveFileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            await copyBlobToClipboard();
            showToast('PNG exported successfully!', 'success');
          } catch (err) {
            // If writing fails, fallback to direct download.
            console.warn('Writing selected file failed, using fallback download.', err);
            await downloadBlobFallback();
            console.error(err);
          }
        } else {
          // Fallback to direct download
          await downloadBlobFallback();
        }
      } finally {
        // Always restore canvas styles even if export/save fails.
        canvasElement.style.transform = originalTransform;
        canvasElement.style.width = originalWidth;
        canvasElement.style.height = originalHeight;
      }

    } catch (error) {
      console.error('PNG export error:', error);
      showToast('Error exporting PNG', 'error');
    }
  }, [showToast, data.nodes, generateFilename]);

  const categoryValidationStats = React.useMemo(() => {
    const stats: Record<string, { total: number; validated: number; pending: number; error: number }> = {};

    data.nodes.forEach((node) => {
      const ciTypeInfo = getCITypeInfo(node.type);
      const category = ciTypeInfo?.category || 'Other';

      if (!stats[category]) {
        stats[category] = { total: 0, validated: 0, pending: 0, error: 0 };
      }

      stats[category].total += 1;

      if (node.validationStatus === 'valid') {
        stats[category].validated += 1;
        return;
      }

      if (
        node.validationStatus === 'invalid' ||
        node.validationStatus === 'not-found' ||
        node.validationStatus === 'needs-selection'
      ) {
        stats[category].error += 1;
        return;
      }

      stats[category].pending += 1;
    });

    return stats;
  }, [data.nodes]);

  const validationSummary = React.useMemo(() => {
    const validated = data.nodes.filter((node) => node.validationStatus === 'valid').length;
    const pending = data.nodes.filter(
      (node) =>
        node.validationStatus === 'pending' ||
        node.validationStatus === 'validating' ||
        node.validationStatus === 'new' ||
        node.validationStatus === 'unknown'
    ).length;
    const issues = data.nodes.filter(
      (node) =>
        node.validationStatus === 'invalid' ||
        node.validationStatus === 'not-found' ||
        node.validationStatus === 'needs-selection'
    ).length;

    const anchorNode = data.nodes.find((node) => node.id === data.metadata.anchorNodeId) || null;
    const hasApplicationId = Boolean(anchorNode?.serviceId?.trim());

    return {
      validated,
      pending,
      issues,
      anchorNode,
      hasApplicationId,
    };
  }, [data.nodes, data.metadata.anchorNodeId]);

  const guidedStep = React.useMemo<'app-id' | 'validate' | 'sync' | 'visit' | null>(() => {
    if (!isGuidedFlowActive || data.nodes.length === 0) {
      return null;
    }

    const anchorNode = data.nodes.find((node) => node.id === data.metadata.anchorNodeId) || null;
    const hasApplicationId = Boolean(anchorNode?.serviceId?.trim());
    const allNodesValidated =
      data.nodes.length > 0 &&
      data.nodes.every((node) => node.validationStatus === 'valid' && node.validatedCI?.sys_id);
    const canOpenInServiceNow = Boolean(
      anchorNode?.validationStatus === 'valid' &&
      anchorNode?.serviceId?.trim() &&
      anchorNode?.validatedCI?.sys_id
    );

    if (!hasApplicationId) {
      return 'app-id';
    }

    if (!allNodesValidated) {
      return 'validate';
    }

    if (!hasGuidedSyncCompleted) {
      return 'sync';
    }

    return canOpenInServiceNow ? 'visit' : 'sync';
  }, [isGuidedFlowActive, data.nodes, data.metadata.anchorNodeId, hasGuidedSyncCompleted]);

  const getSummaryChipClassName = (
    tone: 'success' | 'warning' | 'danger' | 'neutral',
    isActive = false
  ) => {
    if (tone === 'success') {
      return isActive
        ? 'bg-emerald-600 text-white border border-emerald-600 shadow'
        : 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100';
    }

    if (tone === 'warning') {
      return isActive
        ? 'bg-amber-500 text-white border border-amber-500 shadow'
        : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100';
    }

    if (tone === 'danger') {
      return isActive
        ? 'bg-rose-600 text-white border border-rose-600 shadow'
        : 'bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100';
    }

    return isActive
      ? 'bg-slate-700 text-white border border-slate-700 shadow'
      : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200';
  };

  return (
    <TooltipProvider>
      <div className="h-screen overflow-hidden flex flex-col">
        {/* Navigation Header */}
        <header className="w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm z-50">
          <div className="max-w-full mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => window.location.href = '/'}
              className="flex items-center gap-3 hover:opacity-90 transition-opacity group"
            >
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md p-1 border-t-2 border-[#E4002B]">
                <img 
                  src="/images/CVX_Logo_NoTagline_RGB.png" 
                  alt="Chevron Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-sm font-semibold text-[#0054A4] group-hover:text-[#003A73] transition-colors">OT Operations</span>
            </button>

            <div className="h-8 w-px bg-gray-200"></div>
            
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 bg-gradient-to-br from-[#0054A4] to-[#003A73] rounded-md flex items-center justify-center shadow-sm">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="text-sm text-[#0054A4] font-medium">Service Mapper</span>
            </div>
          </div>
          <UserAvatar />
        </div>
        </header>

      {/* Top Panel - Service Mapper with Chevron Styling (Light Mode) */}
        <div className="h-16 bg-white border-b border-gray-200 shadow-sm flex items-center px-6 relative z-sticky">
        {/* Inline Toolbar In Service Mapper Bar */}
        <div className="grid min-w-0 flex-1 grid-cols-[auto_1fr] items-center gap-3">
          <div className="flex items-center gap-1.5 rounded-lg bg-white/90 px-2 py-1">
              <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Menu</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-2 hover:bg-gray-200 rounded-md transition-colors duration-200 flex items-center gap-1"
                    aria-label="Import data options"
                    title="Import Data"
                  >
                    <FileUp className="h-4 w-4 text-gray-600" aria-hidden="true" />
                    <ChevronDown className="h-3 w-3 text-gray-600" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-[12rem] overflow-hidden rounded-xl border border-slate-200/60 bg-white/95 backdrop-blur-[20px] p-2 text-slate-700 shadow-[0_8px_25px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] z-[9999]"
                  side="bottom"
                  sideOffset={2}
                >
                  <DropdownMenuItem onClick={() => setCsvImportOpen(true)}>
                    <FileText className="h-4 w-4 mr-2" />
                    CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleImportJSON}>
                    <FileText className="h-4 w-4 mr-2" />
                    JSON
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setAppIdPopupOpen(true)}>
                    <FileText className="h-4 w-4 mr-2" />
                    Application IDs in Scope
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <LadderDropdownMenu
                onExportCSV={handleExportCSV}
                onExportJSON={handleExportJSON}
                onExportSVG={handleExportSVG}
                onExportPNG={handleExportPNG}
                disabled={data.nodes.length === 0}
              >
                <button
                  className={`p-2 rounded-md transition-colors duration-200 flex items-center gap-1 ${
                    data.nodes.length === 0
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-gray-200'
                  }`}
                  aria-label={data.nodes.length === 0 ? 'Export disabled - add components to export' : 'Export diagram in various formats'}
                  disabled={data.nodes.length === 0}
                >
                  <Download className="h-4 w-4 text-gray-600" aria-hidden="true" />
                  <ChevronDown className="h-3 w-3 text-gray-600" aria-hidden="true" />
                </button>
              </LadderDropdownMenu>
              <button
                type="button"
                onClick={() => setAppIdPopupOpen(true)}
                className="p-2 rounded-md transition-colors duration-200 hover:bg-gray-200"
                title="Open Application ID list"
                aria-label="Open Application ID list"
              >
                <FileText className="h-4 w-4 text-gray-600" aria-hidden="true" />
              </button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      const saveData = handleDataExport();
                      localStorage.setItem('service-mapper-data', JSON.stringify(saveData));
                      showToast('Project saved to browser storage!', 'success');
                    }}
                    className="p-2 hover:bg-gray-200 rounded-md transition-colors duration-200"
                    aria-label="Save project to browser storage"
                  >
                    <Save className="h-4 w-4 text-gray-600" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Save Project...</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleClearRelationships}
                    disabled={data.nodes.length === 0 && data.edges.length === 0}
                    className={`p-2 rounded-md transition-colors duration-200 ${
                      data.nodes.length === 0 && data.edges.length === 0
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:bg-gray-200'
                    }`}
                    aria-label="Clear entire map"
                  >
                    <Trash2 className="h-4 w-4 text-gray-600" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Clear entire map</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowLegend(!showLegend)}
                    className={`p-2 hover:bg-gray-200 rounded-md transition-colors duration-200 ${
                      showLegend ? 'bg-gray-200' : ''
                    }`}
                    aria-label={showLegend ? 'Hide relationship legend' : 'Show relationship legend'}
                  >
                    <CircleHelp className="h-4 w-4 text-gray-600" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle Legend</p>
                </TooltipContent>
              </Tooltip>

            </div>

          <div className="flex shrink-0 items-center gap-1.5 rounded-lg px-1.5 py-0.5 justify-self-end">
            <Suspense fallback={null}>
              <div className="w-[248px]">
                <ControlPanel
                  data={data}
                  onDataImport={handleDataImport}
                  onNodeUpdate={handleNodeUpdate}
                  onEdgeUpdate={handleEdgeUpdate}
                  externalSyncTrigger={externalSyncTrigger}
                  onValidationStateChange={setIsRightPanelBusy}
                  onPanelBusyStateChange={setIsRightPanelBusy}
                  isGuidedFlowActive={isGuidedFlowActive}
                  guidedStep={guidedStep}
                  onGuidedFlowDismiss={() => setIsGuidedFlowActive(false)}
                  onSyncCompleted={() => setHasGuidedSyncCompleted(true)}
                  onFocusAnchorForAppId={handleFocusAnchorForAppId}
                  onClearSelection={() => {
                    setSelectedNode(null);
                    setSelectedNodes(new Set());
                  }}
                  isCompactMode={true}
                />
              </div>
            </Suspense>

            {data.nodes.length > 0 && (
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <button
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${getSummaryChipClassName('success', statusFilter === 'valid')}`}
                  onClick={() => setStatusFilter(statusFilter === 'valid' ? null : 'valid')}
                  title="Click to focus validated components"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {validationSummary.validated} validated
                </button>

                <button
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${getSummaryChipClassName(validationSummary.pending > 0 ? 'warning' : 'neutral', statusFilter === 'pending')}`}
                  onClick={() => setStatusFilter(statusFilter === 'pending' ? null : 'pending')}
                  title="Click to focus pending components"
                >
                  <Clock3 className="h-3.5 w-3.5" />
                  {validationSummary.pending} pending
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (!validationSummary.hasApplicationId) {
                      handleFocusAnchorForAppId();
                    }
                  }}
                  disabled={validationSummary.hasApplicationId}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getSummaryChipClassName(validationSummary.hasApplicationId ? 'success' : 'warning')} ${isGuidedFlowActive && guidedStep === 'app-id' ? 'guidance-pulse guidance-appid' : ''}`}
                  title={
                    validationSummary.anchorNode
                      ? validationSummary.hasApplicationId
                        ? 'Anchor node already has an Application ID'
                        : 'Anchor node is missing the Application ID required for validation and sync'
                      : 'Set an anchor node to enable Application ID tracking'
                  }
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {validationSummary.anchorNode
                    ? validationSummary.hasApplicationId
                      ? 'App ID ready'
                      : 'App ID missing'
                    : 'Anchor required'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Components */}
        <div className={`${isLeftPanelCollapsed ? 'w-12' : 'w-72'} bg-white border-r border-gray-200 shadow-lg flex-shrink-0 overflow-hidden flex flex-col relative z-sticky transition-all duration-300 ease-in-out`} data-ci-palette>
          <div className="absolute top-2 right-2 z-tooltip">
            <button
              onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
              className="w-8 h-8 bg-[#0054A4] hover:bg-[#003A73] text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 border-2 border-[#003A73]"
              title={isLeftPanelCollapsed ? 'Expand components' : 'Collapse components'}
              aria-label={isLeftPanelCollapsed ? 'Expand components panel' : 'Collapse components panel'}
            >
              {isLeftPanelCollapsed ? (
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              ) : (
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              )}
            </button>
          </div>

          {!isLeftPanelCollapsed && (
            <CIPalette
              selectedNode={selectedNode}
              categoryValidationStats={categoryValidationStats}
            />
          )}
        </div>

        {/* Center Panel - Canvas */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Suspense fallback={<div className="flex-1 flex items-center justify-center text-slate-500">Loading canvas...</div>}>
            <DiagramCanvas
            data={data}
            anchorNodeId={data.metadata.anchorNodeId}
            onNodeUpdate={handleNodeUpdate}
            onNodeDelete={handleNodeDelete}
            onEdgeAdd={handleEdgeAdd}
            onEdgeDelete={handleEdgeDelete}
            onEdgeUpdate={handleEdgeUpdate}
            onNodeSelect={setSelectedNode}
            selectedNode={selectedNode}
            selectedNodes={selectedNodes}
            editingConnectionId={editingConnectionId}
            onEditingConnectionIdChange={setEditingConnectionId}
            onMultiSelect={setSelectedNodes}
            onDataImport={handleDataImport}
            onDataExport={handleDataExport}
            onNodeAdd={handleNodeAdd}
            onDataUpdate={setData}
            onEditingStateChange={handleEditingStateChange}
            onFileImport={handleDataImport}
            onSaveAs={handleDataExport}
            onSave={handleDataExport}
            onToggleLegend={() => {}}
            onCopy={handleCopy}
            onPaste={handlePaste}
            onCut={handleCut}
            clipboard={clipboard}
            isNameEditingDisabled={isRightPanelBusy}
            shouldFocusAnchorNode={shouldFitToScreen}
            searchQuery={mapperSearchQuery}
            statusFilter={statusFilter}
            onFitToScreenComplete={handleFitToScreenComplete}
          />
          </Suspense>
        </div>

        {/* Right Panel - Properties + Action Toolbar */}
        <div className="w-72 bg-white border-l border-gray-200 shadow-lg flex-shrink-0 flex flex-col overflow-hidden relative z-sticky transition-all duration-300 ease-in-out">
          <div className="flex-1 overflow-hidden" data-properties-panel>
            <Suspense fallback={<div className="p-4 text-slate-500">Loading properties...</div>}>
              <PropertiesPanel
                selectedNode={selectedNode}
                onNodeUpdate={handleNodeUpdate}
                anchorNodeId={data.metadata.anchorNodeId}
                onSetAnchor={handleSetAnchorNode}
                editingNodeId={editingNodeId}
                editingNodeName={editingNodeName}
                isNameEditingDisabled={isRightPanelBusy}
                isPanelLocked={isRightPanelBusy}
                highlightApplicationId={isGuidedFlowActive && guidedStep === 'app-id'}
                onOpenAnnotationDialog={handleOpenAnnotationDialog}
              />
            </Suspense>
          </div>
        </div>
        </div>

      {/* Service Mapping Help Footer */}
        <div className="border-t border-gray-200 bg-white/80 backdrop-blur-sm px-6 py-2 text-xs text-gray-600 flex items-center justify-end">
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="text-[#0054A4] hover:text-[#003A73] hover:underline inline-flex items-center"
          title="Open feedback form"
        >
          Feedback
        </button>
        <span className="mx-2 text-gray-300" aria-hidden="true">|</span>
        <a
          href="https://dev.azure.com/chevron/IT-FoundationPlatform/_wiki/wikis/IT-DigitalPlatform.wiki/176770/Service-Mapping-Semi-Automated-Tool"
          target="_blank"
          rel="noreferrer"
          className="text-[#0054A4] hover:text-[#003A73] hover:underline inline-flex items-center gap-1"
          title="Open Service Mapping help"
        >
          Help
          <FileText className="h-3 w-3" aria-hidden="true" />
        </a>
        </div>
      </div>
      
      {/* Relationship Legend */}
      {showLegend && (
        <RelationshipLegend onClose={() => setShowLegend(false)} />
      )}

      {/* Support Group Application ID Popup */}
      {appIdPopupOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1200] flex items-start justify-center p-4 pt-20 sm:pt-8">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[calc(100vh-5rem)] overflow-hidden flex flex-col">
            <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[#1E3A5F]">Application IDs In Scope</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Load Application Service IDs for your support group.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAppIdPopupOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close Application ID popup"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 border-b border-gray-100">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Support Group
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={supportGroupInput}
                  onChange={(event) => setSupportGroupInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      void handleLoadScopeSummary();
                    }
                  }}
                  placeholder="e.g. WIPRO-SUBSURFACE-LAND-LAND SOLUTION"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0054A4]/30 focus:border-[#0054A4]"
                />
                <button
                  type="button"
                  onClick={() => void handleLoadScopeSummary()}
                  disabled={isScopeLoading}
                  className="px-4 py-2 bg-[#0054A4] text-white text-sm font-semibold rounded-lg hover:bg-[#003A73] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isScopeLoading ? 'Loading...' : 'Load IDs'}
                </button>
              </div>

              {scopeSummary && (
                <p className="text-xs text-gray-500 mt-2">
                  {scopeSummary.totalApplicationServices} services in scope for{' '}
                  <span className="font-semibold text-[#0054A4]">{scopeSummary.supportGroup}</span>
                </p>
              )}

              {scopeError && (
                <p className="text-xs text-red-600 mt-2">{scopeError}</p>
              )}
            </div>

            <div className="flex-1 overflow-auto">
              {!scopeSummary && !isScopeLoading && !scopeError && (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  Enter your support group and click Load IDs.
                </div>
              )}

              {isScopeLoading && (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  Fetching Application IDs...
                </div>
              )}

              {scopeSummary && !isScopeLoading && applicationIdRows.length === 0 && (
                <div className="px-5 py-8 text-center text-sm text-gray-500">
                  No Application Service IDs were found for this support group.
                </div>
              )}

              {scopeSummary && !isScopeLoading && applicationIdRows.length > 0 && (
                <>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-y border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="text-left px-5 py-3">Application ID</th>
                        <th className="text-left px-4 py-3">Service Name</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3">Coverage</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedApplicationIdRows.map((row) => (
                        <tr key={`${row.applicationId}-${row.serviceName}`} className="hover:bg-gray-50">
                          <td className="px-5 py-3 text-xs font-mono text-gray-700">{row.applicationId}</td>
                          <td className="px-4 py-3 text-gray-700">{row.serviceName}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium text-gray-700 border-gray-200 bg-gray-50">
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500">{row.coveragePercent}%</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => handleLoadMappingForApplicationId(row)}
                              className="p-1.5 text-white bg-[#0054A4] hover:bg-[#003A73] rounded-md transition-colors"
                              title="Load Mapping"
                              aria-label={`Load mapping for ${row.applicationId}`}
                            >
                              <Download className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
                    <p className="text-xs text-gray-500">
                      Showing {visibleRowsStart}-{visibleRowsEnd} of {applicationIdRows.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setApplicationIdPage((page) => Math.max(1, page - 1))}
                        disabled={applicationIdPage === 1}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-600 min-w-[72px] text-center">
                        Page {applicationIdPage} / {totalApplicationIdPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setApplicationIdPage((page) => Math.min(totalApplicationIdPages, page + 1))}
                        disabled={applicationIdPage >= totalApplicationIdPages}
                        className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* CSV Import Dialog */}
      <CSVImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={(importedData) => {
          handleDataImport(importedData);
          if (!hasSeenCsvGuide) {
            setIsGuidedFlowActive(true);
            setHasGuidedSyncCompleted(false);
            setHasSeenCsvGuide(true);
            try {
              localStorage.setItem(CSV_GUIDE_SEEN_STORAGE_KEY, 'true');
            } catch {
              // Ignore storage failures and keep session behavior.
            }
          } else {
            setIsGuidedFlowActive(false);
          }
          setCsvImportOpen(false);
          showToast('CSV data imported successfully!', 'success');
        }}
      />

      {/* Annotation Dialog */}
      <AnnotationDialog
        open={annotationDialogOpen}
        onClose={() => setAnnotationDialogOpen(false)}
        node={annotationNodeId ? data.nodes.find(n => n.id === annotationNodeId) || null : null}
        onSave={handleSaveComment}
      />

      {/* Relationship Properties Dialog */}
      <RelationshipPropertiesDialog
        open={editingConnectionId !== null}
        onClose={() => setEditingConnectionId(null)}
        edge={editingConnectionId ? data.edges.find(e => e.id === editingConnectionId) || null : null}
        onUpdate={handleEdgeUpdate}
        nodes={data.nodes}
      />

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />

      {/* API Test Component */}
      {showApiTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-overlay flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                🔌 .NET API Test Panel
              </h2>
              <button
                onClick={() => setShowApiTest(false)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Close API test panel"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <ApiTestComponent />
            </div>
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
